import React, { useState, useEffect } from 'react';
import useIsMobile from '../../../hooks/useIsMobile';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { Button, Divider, Typography, message } from 'antd';
import { CaretDownOutlined, CaretUpOutlined, SoundFilled } from '@ant-design/icons';
import useAudioCache from '../../../hooks/useAudioCache';
import useTranslation from '../../../hooks/useTranslation';

const { Text } = Typography;

/** Динамик у заголовка: одна кнопка на оба положения — рядом с заголовком и рядом с транскрипцией. */
const SpeakerButton = ({ label, onPlay }) => (
    <button
        type="button"
        aria-label={label}
        style={{
            borderRadius: '25%',
            padding: '3px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
            background: 'none',
            border: '1.5px solid #838383',
            marginRight: '8px',
        }}
        onClick={onPlay}
    >
        <SoundFilled style={{ fontSize: '0.9em', color: '#838383' }} />
    </button>
);

function SelfAssessmentWordCard({
                      word,
                      handleCorrectClick,
                      handleIncorrectClick,
                      locale,
                  }) {
    const isMobile = useIsMobile();
    const { t } = useTranslation();
    const { playAudio } = useAudioCache(
        `${word.headword}|${word.partOfSpeech}|${locale.code}`, locale.code);
    const [showDefinition, setShowDefinition] = useState(false);
    const [openExamples, setOpenExamples] = useState({});

    useEffect(() => {
        setShowDefinition(false);
        setOpenExamples({});
    }, [word]);

    const toggleExamples = (index) => {
        setOpenExamples((prevState) => ({
            ...prevState,
            [index]: !prevState[index],
        }));
    };

    // Озвучка слова проигрывается при показе карточки — поведение практики
    // сохранено, теперь по ref из ответа и через общий кэш useAudioCache.
    useEffect(() => {
        if (word.audio?.access === 'ALLOWED') {
            playAudio(word.audio.ref);
        }
    }, [word, playAudio]);



    /**
     * Клик по карточке раскрывает определение — кроме нажатий на элементы управления
     * внутри неё. Раньше от них закрывался щит `stopPropagation` вокруг шапки, и заодно
     * он глушил нажатие по самому слову: ткнув в заголовок, человек не получал ничего.
     * <p>
     * Обработчик висит на самой карточке, а для клавиатуры и читалки под разделителем
     * стоит настоящая кнопка: контейнер с tabIndex без роли обещал бы читалке действие,
     * которого у него нет, а роль кнопки ему нельзя — внутри уже есть кнопки озвучки.
     */
    const revealDefinition = (event) => {
        if (event.target.closest('button, a, input, [role="button"]')) {
            return;
        }
        setShowDefinition(true);
    };

    const playHeadword = (event) => {
        event.stopPropagation();
        playAudio(word.audio?.ref, word.audio?.access);
    };

    // Замок в данных: PLAN_REQUIRED объясняется словами, а не тишиной.
    const playExampleAudio = (example) => {
        if (example.audio?.access === 'ALLOWED' || example.audio?.access === 'NOT_READY') {
            playAudio(example.audio.ref, example.audio.access);
        } else if (example.audio?.access === 'PLAN_REQUIRED') {
            message.info(t('common.exampleAudioPremium'));
        }
    };

    return (
        <div
            style={{
                padding: isMobile ? '0' : '0px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                minHeight: 'calc(100vh - 80px)',
                overflow: 'hidden',
                width: isMobile ? '100%' : '33%',
                margin: isMobile ? '0' : 'auto',
            }}
        >
            <Card
                onClick={revealDefinition}
                style={{
                    flex: '1 1 auto',
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '0px'
                }}
            >
                <CardContent style={{ flexGrow: 1, padding: isMobile ? '0px' : '20px' }}>

                    <div>
                        {/* Транскрипция есть — динамик стоит в строке с ней; нет — рядом с
                            заголовком. Раньше это были два компонента, отличавшиеся только
                            этим, и у слова без транскрипции печаталось пустое «//». */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '0px' }}>
                            <div data-testid="headword-row"
                                 style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {!word.phonetic && <SpeakerButton label={t('common.listen')} onPlay={playHeadword} />}
                                <Text strong style={{ fontSize: '2em', color: '#027b83', textAlign: 'center' }}>
                                    {word.headword}
                                </Text>
                            </div>
                        </div>
                        {word.phonetic && (
                            <div data-testid="phonetic-row"
                                 style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <SpeakerButton label={t('common.listen')} onPlay={playHeadword} />
                                <Text style={{ fontSize: '1em', marginLeft: '8px' }}>/{word.phonetic}/</Text>
                            </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <Text strong style={{ fontSize: '1.2em', marginTop: '8px', textAlign: 'center' }}>
                                {word.partOfSpeech}
                            </Text>
                        </div>
                    </div>

                    <Divider style={{ margin: isMobile ? '0px 0' : '10px 0' }} />

                    {!showDefinition && (
                        <button
                            type="button"
                            aria-expanded={false}
                            onClick={() => setShowDefinition(true)}
                            style={{
                                display: 'block', width: '100%', background: 'none', border: 'none',
                                padding: '8px 0', color: '#8e949a', cursor: 'pointer', font: 'inherit', fontSize: '0.9em',
                            }}
                        >
                            {t('practice.run.showDefinition')}
                        </button>
                    )}

                    {showDefinition && (
                        <div style={{ marginTop: '10px' }}>
                            {word.wordDefinition.map((def, index) => (
                                <div key={def.id} style={{ marginBottom: '16px' }}>
                                    <button
                                        type="button"
                                        aria-expanded={Boolean(openExamples[index])}
                                        onClick={() => toggleExamples(index)}
                                        style={{
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            background: 'none', border: 'none', padding: 0, font: 'inherit', textAlign: 'left', width: '100%'
                                        }}
                                    >
                                        {openExamples[index] ?
                                            <CaretUpOutlined style={{ marginLeft: '8px', color: '#d9d9d9' }} /> :
                                            <CaretDownOutlined style={{ marginLeft: '8px', color: '#d9d9d9' }} />}

                                        <Text strong style={{ marginLeft: '8px' }}>{def.definition}</Text>
                                    </button>
                                    <div style={{ marginTop: '4px' }}>
                                        <Text italic strong style={{ marginLeft: '36px', color: '#ee6002' }}>
                                            {def.translation}
                                        </Text>
                                    </div>

                                    {openExamples[index] && (
                                        <div style={{ marginTop: '10px', marginLeft: '16px' }}>
                                            {def.examples.map((example, exampleIndex) => (
                                                <div key={example.id} style={{ marginBottom: '8px' }}>
                                                    <button
                                                        type="button"
                                                        aria-label={t('common.listen')}
                                                        style={{
                                                            border: '1px solid #027b83',
                                                            borderRadius: '25%',
                                                            padding: '3px',
                                                            display: 'inline-flex',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                            cursor: 'pointer',
                                                            background: 'none',
                                                            marginRight: '8px',
                                                        }}
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            playExampleAudio(example);
                                                        }}
                                                    >
                                                        <SoundFilled style={{ fontSize: '0.6em', color: '#027b83' }} />
                                                    </button>
                                                    <Text>{example.sentence}</Text>
                                                    <br />
                                                    <Text type="secondary">{example.translation}</Text>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>

                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        paddingTop: '6px',
                        paddingLeft: '20px',
                        paddingBottom: '20px',
                        paddingRight: '20px',
                    }}
                >
                    <Button
                        onClick={handleCorrectClick}
                        style={{ width: isMobile ? '100%' : '150px', marginRight: '10px' }}
                    >
                        Correct
                    </Button>
                    <Button
                        onClick={handleIncorrectClick}
                        style={{ width: isMobile ? '100%' : '150px' }}
                    >
                        Incorrect
                    </Button>
                </div>
            </Card>
        </div>
    );
}

export default SelfAssessmentWordCard;
